import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import type { Bundle } from '../types/bundle';
import { supabase } from '../lib/supabase';

type BundlesState = { bundles: Bundle[]; loading: boolean };
const defaultState: BundlesState = { bundles: [], loading: true };
const BundlesContext = createContext<BundlesState>(defaultState);

export function BundlesProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<BundlesState>(defaultState);

  useEffect(() => {
    (async () => {
      try {
        const { data: bundlesData, error: bundlesErr } = await supabase
          .from('bundles')
          .select('*')
          .eq('is_available', true)
          .order('created_at', { ascending: false });
        if (bundlesErr) {
          setState({ bundles: [], loading: false });
          return;
        }
        const bundleRows = bundlesData ?? [];
        const { data: itemsData } = await supabase.from('bundle_items').select('bundle_id, product_id, sort_order');
        const orderedByBundle = new Map<string, string[]>();
        (
          (itemsData ?? []) as { bundle_id: string; product_id: string; sort_order: number }[]
        )
          .sort((a, b) => a.sort_order - b.sort_order)
          .forEach((r) => {
            const list = orderedByBundle.get(r.bundle_id) ?? [];
            list.push(r.product_id);
            orderedByBundle.set(r.bundle_id, list);
          });
        const bundles: Bundle[] = bundleRows.map((r: Record<string, unknown>) => ({
          id: r.id as string,
          slug: r.slug as string,
          name: r.name as string,
          description: r.description as string,
          price: Number(r.price),
          originalPrice: Number(r.original_price),
          tag: (r.tag as string) ?? undefined,
          theme: r.theme as 'red' | 'blue',
          image: (r.image_url as string) ?? undefined,
          isAvailable: r.is_available !== false,
          productIds: orderedByBundle.get(r.id as string) ?? [],
        }));
        setState({ bundles, loading: false });
      } catch {
        setState({ bundles: [], loading: false });
      }
    })();
  }, []);

  return <BundlesContext.Provider value={state}>{children}</BundlesContext.Provider>;
}

function useBundlesState() {
  const ctx = useContext(BundlesContext);
  return ctx ?? defaultState;
}

export function useBundles() {
  const { bundles, loading } = useBundlesState();
  return useMemo(() => {
    const getBundles = () => bundles;
    const getBundleBySlug = (slug: string | undefined) =>
      slug ? bundles.find((b) => b.slug === slug) ?? null : null;
    const getBundleById = (id: string | undefined) =>
      id ? bundles.find((b) => b.id === id) ?? null : null;
    return { bundles, loading, getBundles, getBundleBySlug, getBundleById };
  }, [bundles, loading]);
}
